// LandlordToolkitPage.tsx - Main toolkit page with authentication-based routing
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../auth/firebaseAuth';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import LandlordToolkitLanding from './LandlordToolkitLanding';
import LandlordToolkitDashboard from './LandlordToolkitDashboard';
import { remoteConfig } from '../../utility/firebaseRemoteConfigHelper';
import LandlordToolkitComingSoon from './LandlordToolkitComingSoon';

const LandlordToolkitPage: React.FC = () => {
  const { currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();
  const [isLandlordToolKitEnabled, setIsLandlordToolKitEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      await remoteConfig.ensureInitialized();
      const enabled = remoteConfig.isLandlordToolKitEnabled();
      setIsLandlordToolKitEnabled(enabled);
      setIsLoading(false);
    };

    fetchConfig();
  }, []);

  // Track toolkit page view
  useEffect(() => {
    if (!isLoading) {
      analytics.trackPixelPageView({
        email: currentUser?.email || undefined,
        externalId: currentUser?.uid || undefined,
      });
      analytics.trackInteraction('landlord_toolkit_page_viewed', {
        authenticated: !!currentUser,
        user_role: currentUser ? 'landlord' : 'unauthenticated',
        enabled: isLandlordToolKitEnabled,
        timestamp: new Date().toISOString(),
      });
    }
  }, [isLoading, currentUser, analytics, isLandlordToolKitEnabled]);

  // SEO Helmet block
  const seoTitle = currentUser
    ? 'Landlord Toolkit Dashboard | RentSmart.ie'
    : 'Landlord Toolkit for Irish Landlords | RentSmart.ie';
  const seoDescription = currentUser
    ? 'Access your personalized landlord dashboard with tools for rent tracking, compliance, and property management. Exclusive to Irish landlords.'
    : 'Discover RentSmart’s Landlord Toolkit: free tools for rent tracking, RPZ compliance, document management, and more. Designed for Irish landlords.';

  if (isLoading) {
    return null; // or a loading spinner
  }

  if (!isLandlordToolKitEnabled) {
    return (
      <>
        <Helmet>
          <title>Landlord Toolkit Coming Soon | RentSmart.ie</title>
          <meta
            name="description"
            content="RentSmart’s Landlord Toolkit is launching soon. Get notified about new tools for Irish landlords including rent tracking, RPZ compliance, and more."
          />
          <meta
            name="keywords"
            content="landlord toolkit, rent tracker, RPZ compliance, property management, Ireland, landlord tools"
          />
          <link rel="canonical" href="https://rentsmart.ie/landlord-toolkit" />
          <meta property="og:title" content="Landlord Toolkit Coming Soon | RentSmart.ie" />
          <meta
            property="og:description"
            content="Powerful new tools for Irish landlords are coming soon to RentSmart.ie. Stay tuned!"
          />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://rentsmart.ie/landlord-toolkit" />
        </Helmet>
        <LandlordToolkitComingSoon />
      </>
    );
  }

  // Show dashboard if authenticated, landing page if not
  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta
          name="keywords"
          content="landlord toolkit, rent tracker, RPZ compliance, property management, Ireland, landlord dashboard, landlord tools"
        />
        <link rel="canonical" href="https://rentsmart.ie/landlord-toolkit" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/landlord-toolkit" />
      </Helmet>
      {currentUser ? <LandlordToolkitDashboard /> : <LandlordToolkitLanding />}
    </>
  );
};

export default LandlordToolkitPage;
